import AjvDraft04 from "ajv-draft-04";
import { JobsConfigurationCommand } from "modules/system/sections/plt/field-management/rings/rollout-package/configure/jobs/dtos";
import { useMemo } from "react";

import {
  CenteredBox,
  FormState,
  Text,
  useFieldContext,
  useFieldRegister
} from "@bps/fluent-ui";
import { ErrorAlert } from "@components/errors/ErrorAlert";
import { CustomRJSFSchema } from "@libs/api/gateways/field/field-ops-gateway.dtos";
import { withTheme } from "@rjsf/core";
import { Theme as FluentUITheme } from "@rjsf/fluent-ui";
import { customizeValidator } from "@rjsf/validator-ajv8";

// Undefined values, when JSON.stringify'd, are removed from the schema.
// We'd like to retain them as empty values are legitimate unless the schema defines otherwise.
function stringifyReplacer(_: any, value: any) {
  return value === undefined ? null : value;
}

const JsonForm = withTheme(FluentUITheme);

// We are using the ajv draft 04 validator which is generated by backend.
const validator = customizeValidator({ AjvClass: AjvDraft04 });

interface CommandSchemaFormProps {
  schemas: CustomRJSFSchema[];
  formState: FormState<JobsConfigurationCommand, Error>;
}

export const CommandSchemaForm: React.FC<CommandSchemaFormProps> = ({
  schemas,
  formState
}) => {
  useFieldRegister("message");
  const { value: messageValue, setFieldValue } = useFieldContext<
    string | undefined
  >("message");

  const schema = schemas.find(x => x.title == formState.values.commandName);

  const messageObject = useMemo(
    () => convertPropertyNameCasingToSchemaPropertyCasing(messageValue, schema),
    [messageValue, schema]
  );

  // Ensure json is valid in the first place
  try {
    JSON.parse(messageValue ?? "{}");
  } catch {
    return (
      <CenteredBox>
        <ErrorAlert error="Command message JSON was invalid. You may need to reset your configuration to default values." />
      </CenteredBox>
    );
  }

  if (!schema)
    return (
      <CenteredBox>
        <Text>Please select a command name.</Text>
      </CenteredBox>
    );

  const innerFormChange = (formData: JobsConfigurationCommand) => {
    const pascalCaseMessageValue = JSON.stringify(
      messageObject,
      stringifyReplacer
    );
    if (!!formData && !!pascalCaseMessageValue) {
      const formDataString = JSON.stringify(formData, stringifyReplacer);
      if (formDataString !== pascalCaseMessageValue) {
        setFieldValue(formDataString);
      }
    }
  };

  return (
    <JsonForm
      schema={schema}
      tagName="div"
      formData={messageObject}
      onChange={a => {
        innerFormChange(a.formData);
      }}
      validator={validator}
    >
      <div style={{ display: "none" }} />
    </JsonForm>
  );
};

function convertPropertyNameCasingToSchemaPropertyCasing(
  messageValue: string | undefined,
  schema: CustomRJSFSchema | undefined
) {
  if (!schema || !messageValue) return {};

  // A unique problem we have is that our commands.json schema property values are in PascalCase (first letter uppercase)
  // but the command configuration values generated from the backend models are in camelCase (first letter lowercase).
  // We have to adapt the configuration properties to PascalCase to match our schema.

  // Following the merge of PR's for PBI #53392, json schema properties will be in camelCase and will match the default configuration
  // values (also in camelCase). However, we might still have a saved configuration altered to be PascalCase from the old json schema properties.
  // So, we need to check our configuration property names for PascalCase and convert them to camelCase to match our new json schema property casing.

  let message = {};
  try {
    message = JSON.parse(messageValue);
  } catch {
    return message;
  }

  const messageKeys = Object.keys(message);
  if (messageKeys.length === 0)
    // On the off chance we have no message properties at all, return it as-is
    return message;

  const firstMessagePropertyFirstCharacter = messageKeys[0][0];

  const isConfigurationPascalCase =
    firstMessagePropertyFirstCharacter ===
    firstMessagePropertyFirstCharacter.toLocaleUpperCase();

  const properties = schema.properties;

  if (!properties || Object.keys(properties).length === 0) return message;

  const firstSchemaPropertyFirstCharacter = Object.keys(properties)[0][0];

  const isSchemaPascalCase =
    firstSchemaPropertyFirstCharacter ===
    firstSchemaPropertyFirstCharacter.toLocaleUpperCase();

  // Both are camelCase or both are PascalCase, no changes necessary
  if (isConfigurationPascalCase === isSchemaPascalCase) return message;

  return isConfigurationPascalCase
    ? convertCamelCasePropertiesToPascalCase(message)
    : convertPascalCasePropertiesToCamelCase(message);
}

function convertToPascalCase(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

function convertToCamelCase(str: string): string {
  return str.charAt(0).toLowerCase() + str.slice(1);
}

function convertCamelCasePropertiesToPascalCase(obj: any) {
  const pascalCaseProperties: Record<string, typeof obj[keyof typeof obj]> = {};

  for (const [key, value] of Object.entries(obj)) {
    pascalCaseProperties[convertToPascalCase(key)] = value;
  }

  return {
    ...pascalCaseProperties
  };
}

function convertPascalCasePropertiesToCamelCase(obj: any) {
  const camelCaseProperties: Record<string, typeof obj[keyof typeof obj]> = {};

  for (const [key, value] of Object.entries(obj)) {
    camelCaseProperties[convertToCamelCase(key)] = value;
  }

  return {
    ...camelCaseProperties
  };
}
